import gql from 'graphql-tag';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Datagrid,
  EditButton,
  FunctionField,
  TextField,
  useCreate,
  useGetList,
  useNotify,
  useRecordContext,
  useRedirect,
  WrapperField,
} from 'react-admin';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { Sync as SyncIcon } from '@mui/icons-material';

import client from 'src/data/clientApiClient';
import adminApiClient from 'src/data/api';
import LoadingOverlay from 'src/components/LoadingOverlay';
import UnauthorizedSectionContent from 'src/components/UnauthorizedSectionContent';
import {
  CompanySubscriptionSetConfigRecord,
  CompanySubscriptionSetRecord,
  ExternalApiClientRecord,
  OrganizationRecord,
} from 'src/types';
import { CLIENT_API_API_KEY_HEADER_NAME, IS_LOCAL_OR_QA_ENV } from 'src/utils/defaults/Constants';
import { CraftPageSection } from 'src/components/CraftPageSection';
import { usePermissions } from 'src/hooks';
import { useState } from 'react';
import { getCompanySubscriptionSetConfigIdKey } from 'src/utils/idKey';

/** Staging subscription set list; includes companyIds and dataPackages for sync-to-QA. */
const clientCompanySubscriptionSetsGQL = gql`
  query Client {
    client {
      companySubscriptionSets {
        id
        name
        description
        companyLimit
        allCompanies
        companyIds
        dataPackages {
          id
        }
      }
    }
  }
`;

/** Check if an external API client with this token already exists in QA (non-archived). */
const externalApiClientByTokenGQL = gql`
  query external_api_clients_by_token($token: String!) {
    external_api_clients(where: { token: { _eq: $token }, archived_at: { _is_null: true } }, limit: 1) {
      id
    }
  }
`;

/** Create an external API client in QA with the same API key as the Staging client (sync from Staging). */
const insertExternalApiClientGQL = gql`
  mutation insert_external_api_clients_one($object: external_api_clients_insert_input!) {
    insert_external_api_clients_one(object: $object) {
      id
      token
    }
  }
`;

/** Create one company subscription set in QA (sync from Staging). */
const insertCompanySubscriptionSetGQL = gql`
  mutation insert_company_subscription_sets($objects: [company_subscription_sets_insert_input!]!) {
    insert_company_subscription_sets(objects: $objects) {
      returning {
        id
      }
    }
  }
`;

/** Link data packages to a company subscription set in QA. */
const insertCompanySubscriptionSetDataPackagesGQL = gql`
  mutation insert_company_subscription_sets_data_packages(
    $objects: [company_subscription_sets_data_packages_insert_input!]!
  ) {
    insert_company_subscription_sets_data_packages(objects: $objects) {
      affected_rows
    }
  }
`;

/** Create company subscriptions in QA (companies in a subscription set). */
const insertCompanySubscriptionsGQL = gql`
  mutation insert_company_subscriptions($objects: [company_subscriptions_insert_input!]!) {
    insert_company_subscriptions(
      objects: $objects
      on_conflict: { constraint: company_subscriptions_ci_cssi_unique, update_columns: [] }
    ) {
      affected_rows
    }
  }
`;

/** Fetch QA subscription sets by external API client id (includes soft-deleted for full sync). */
const companySubscriptionSetsByClientIdGQL = gql`
  query company_subscription_sets_by_client($externalApiClientId: Int!) {
    company_subscription_sets(where: { external_api_client_id: { _eq: $externalApiClientId } }) {
      id
      name
      deleted_at
    }
  }
`;

/** Delete company subscriptions for a set (before re-syncing from Staging). */
const deleteCompanySubscriptionsGQL = gql`
  mutation delete_company_subscriptions($where: company_subscriptions_bool_exp!) {
    delete_company_subscriptions(where: $where) {
      affected_rows
    }
  }
`;

/** Delete data package links for a set (before re-syncing from Staging). */
const deleteCompanySubscriptionSetDataPackagesGQL = gql`
  mutation delete_company_subscription_sets_data_packages($where: company_subscription_sets_data_packages_bool_exp!) {
    delete_company_subscription_sets_data_packages(where: $where) {
      affected_rows
    }
  }
`;

/** Update a company subscription set in QA. */
const updateCompanySubscriptionSetGQL = gql`
  mutation update_company_subscription_sets(
    $where: company_subscription_sets_bool_exp!
    $_set: company_subscription_sets_set_input!
  ) {
    update_company_subscription_sets(where: $where, _set: $_set) {
      affected_rows
    }
  }
`;

/** Staging API subscription set shape (includes companyIds and dataPackages for sync). */
type StagingSubscriptionSet = {
  id: string;
  name: string;
  description?: string | null;
  companyLimit?: number | null;
  allCompanies: boolean;
  companyIds?: string[] | null;
  dataPackages: { id: string }[];
};

const editButtonLabel = 'Edit Settings';

/**
 * @todo handle deleted configs
 */
const CompanySubscriptionSetSettingsList = () => {
  const organizationRecord = useRecordContext<OrganizationRecord>();
  const redirect = useRedirect();
  const notify = useNotify();

  const [isQaOrgWithoutStagingClient, setIsQaOrgWithoutStagingClient] = useState(false);
  const [syncToQALoading, setSyncToQALoading] = useState(false);

  const [checkExistingClientByToken] = useLazyQuery<{
    external_api_clients: { id: number }[];
  }>(externalApiClientByTokenGQL, {
    client: adminApiClient,
    fetchPolicy: 'network-only',
  });

  const [insertExternalApiClient] = useMutation<{
    insert_external_api_clients_one: { id: number; token: string | null };
  }>(insertExternalApiClientGQL, {
    client: adminApiClient,
    errorPolicy: 'all',
  });

  const [insertCompanySubscriptionSet] = useMutation<{
    insert_company_subscription_sets: { returning: { id: number }[] };
  }>(insertCompanySubscriptionSetGQL, {
    client: adminApiClient,
    errorPolicy: 'all',
  });

  const [insertCompanySubscriptionSetDataPackages] = useMutation<{
    insert_company_subscription_sets_data_packages: { affected_rows: number };
  }>(insertCompanySubscriptionSetDataPackagesGQL, {
    client: adminApiClient,
    errorPolicy: 'all',
  });

  const [insertCompanySubscriptions] = useMutation<{
    insert_company_subscriptions: { affected_rows: number };
  }>(insertCompanySubscriptionsGQL, {
    client: adminApiClient,
    errorPolicy: 'all',
  });

  const [fetchQASetsByClientId] = useLazyQuery<{
    company_subscription_sets: { id: number; name: string; deleted_at: string | null }[];
  }>(companySubscriptionSetsByClientIdGQL, {
    client: adminApiClient,
    fetchPolicy: 'network-only',
  });

  const [deleteCompanySubscriptions] = useMutation<{
    delete_company_subscriptions: { affected_rows: number };
  }>(deleteCompanySubscriptionsGQL, {
    client: adminApiClient,
    errorPolicy: 'all',
  });

  const [deleteCompanySubscriptionSetDataPackages] = useMutation<{
    delete_company_subscription_sets_data_packages: { affected_rows: number };
  }>(deleteCompanySubscriptionSetDataPackagesGQL, {
    client: adminApiClient,
    errorPolicy: 'all',
  });

  const [updateCompanySubscriptionSet] = useMutation<{
    update_company_subscription_sets: { affected_rows: number };
  }>(updateCompanySubscriptionSetGQL, {
    client: adminApiClient,
    errorPolicy: 'all',
  });

  // (QA env only) fetch subscription sets from Client API
  // TODO: Once Portal starts using QA Client API, use Admin API if this view isn't moved
  const {
    data: companySubscriptionSetsFromClientApiData,
    loading: companySubscriptionSetsFromClientApiLoading,
    error: companySubscriptionSetsFromClientApiError,
  } = useQuery<{ client: { companySubscriptionSets: StagingSubscriptionSet[] } }>(clientCompanySubscriptionSetsGQL, {
    client,
    // this query is only needed in the local and QA envs, to get subscription sets from Staging
    skip: !IS_LOCAL_OR_QA_ENV,
    context: {
      headers: { [CLIENT_API_API_KEY_HEADER_NAME]: organizationRecord?.company_data_api_key },
    },
    // we don't want to cache this response, as the request headers are set for each org. that is
    // loaded, but Apollo Client doesn't consider the headers when resolving a cache key
    fetchPolicy: 'no-cache',
  });

  if (companySubscriptionSetsFromClientApiError) {
    console.error(companySubscriptionSetsFromClientApiError);
    // If we get a 401 error from Client API, we'll assume that the key assigned to this org.
    // doesn't match an active API client (from Staging Client API).
    if (companySubscriptionSetsFromClientApiError.networkError?.message.includes('401')) {
      if (!isQaOrgWithoutStagingClient) setIsQaOrgWithoutStagingClient(true);
    } else {
      notify('Error fetching API client data', { type: 'error' });
    }
  }

  const {
    data: externalApiClientArr,
    isLoading: externalApiClientLoading,
    error: externalApiClientError,
  } = useGetList<ExternalApiClientRecord>(
    'external_api_clients',
    {
      filter: {
        'token@_eq': organizationRecord?.company_data_api_key,
        'archived_at@_is_null': true,
      },
      pagination: { page: 1, perPage: 1 },
    },
    // we don't reference this in QA while QA orgs use Staging API clients
    { enabled: !IS_LOCAL_OR_QA_ENV },
  );
  // should only return 1 record
  const externalApiClient = externalApiClientArr?.[0];

  // fetch subscription sets from Craft Admin API
  const {
    data: companySubscriptionSetsFromAdminAPI,
    isLoading: companySubscriptionSetsFromAdminAPILoading,
    error: companySubscriptionSetsFromAdminAPIError,
  } = useGetList<CompanySubscriptionSetRecord>(
    'company_subscription_sets',
    {
      filter: {
        external_api_client_id: externalApiClient?.id,
        all_companies: false,
        'deleted_at@_is_null': true,
      },
      meta: { noLimit: true },
    },
    // we get this data from a different query in QA while QA orgs use Staging API clients
    { enabled: !IS_LOCAL_OR_QA_ENV && !!externalApiClient },
  );

  const {
    data: companySubscriptionSetConfigs,
    isLoading: companySubscriptionSetConfigsLoading,
    error: companySubscriptionSetConfigsError,
  } = useGetList<CompanySubscriptionSetConfigRecord>('company_subscription_set_configs', {
    filter: { organization_id: organizationRecord?.id },
    meta: { noLimit: true },
  });

  if (externalApiClientError ?? companySubscriptionSetsFromAdminAPIError ?? companySubscriptionSetConfigsError) {
    console.error(
      externalApiClientError ?? companySubscriptionSetsFromAdminAPIError ?? companySubscriptionSetConfigsError,
    );
    notify('Error fetching company subscription set data', { type: 'error' });
  }

  const [createCompanySubscriptionSetConfig, { isLoading: createCompanySubscriptionSetConfigLoading }] = useCreate();

  if (!organizationRecord) return null;

  /**
   * Sync subscription sets and company subscriptions from Staging into QA.
   * Ensures an external API client exists, then creates/updates/soft-deletes sets to match Staging.
   *
   * NOTE: Sets are matched by name. Renaming a set in Staging will cause the old QA set to be
   * soft-deleted and a new one to be created (different ID). This does not affect the ability to
   * sync company subscriptions to Databricks — the companies are recreated under the new set and
   * still flow through the CDC pipeline.
   */
  const handleSyncToQA = async () => {
    const apiKey = organizationRecord?.company_data_api_key;
    if (!apiKey) {
      notify('Organization has no API key to sync', { type: 'error' });
      return;
    }
    setSyncToQALoading(true);
    try {
      const { data: existingData } = await checkExistingClientByToken({ variables: { token: apiKey } });
      const existingClient = existingData?.external_api_clients?.[0];
      let externalApiClientId: number;

      if (existingClient) {
        externalApiClientId = existingClient.id;
      } else {
        const now = new Date().toISOString();
        const { data: clientData, errors: clientErrors } = await insertExternalApiClient({
          variables: {
            object: {
              token: apiKey,
              name: `Staging sync - Org ${organizationRecord?.name ?? organizationRecord?.id ?? 'unknown'}`,
              client_type: 'internal',
              created_at: now,
              updated_at: now,
            },
          },
        });
        if (clientErrors?.length) {
          notify(clientErrors[0].message || 'Failed to create external API client in QA', { type: 'error' });
          return;
        }
        const newId = clientData?.insert_external_api_clients_one?.id;
        if (newId == null) {
          notify('Sync failed: no client ID returned', { type: 'error' });
          return;
        }
        externalApiClientId = newId;
      }

      const stagingSets: StagingSubscriptionSet[] =
        companySubscriptionSetsFromClientApiData?.client?.companySubscriptionSets ?? [];
      const stagingSetNames = new Set(stagingSets.map((s) => s.name));

      const { data: qaSetsData } = await fetchQASetsByClientId({
        variables: { externalApiClientId },
      });
      const existingQASets: { id: number; name: string; deleted_at: string | null }[] =
        qaSetsData?.company_subscription_sets ?? [];

      const now = new Date().toISOString();
      let setsCreated = 0;
      let setsUpdated = 0;
      let setsDeleted = 0;
      let companySubsCreated = 0;

      for (const set of stagingSets) {
        const existingQASet = existingQASets.find((q) => q.name === set.name);
        let setId: number;

        if (existingQASet) {
          setId = existingQASet.id;
          await deleteCompanySubscriptions({
            variables: { where: { company_subscription_set_id: { _eq: setId } } },
          });
          await deleteCompanySubscriptionSetDataPackages({
            variables: { where: { company_subscription_set_id: { _eq: setId } } },
          });
          await updateCompanySubscriptionSet({
            variables: {
              where: { id: { _eq: setId } },
              _set: {
                description: set.description ?? null,
                company_limit: set.companyLimit ?? null,
                all_companies: set.allCompanies,
                deleted_at: null,
                updated_at: now,
              },
            },
          });
          setsUpdated += 1;
        } else {
          const { data: setData, errors: setErrors } = await insertCompanySubscriptionSet({
            variables: {
              objects: [
                {
                  external_api_client_id: externalApiClientId,
                  name: set.name,
                  description: set.description ?? null,
                  company_limit: set.companyLimit ?? null,
                  all_companies: set.allCompanies,
                },
              ],
            },
          });
          if (setErrors?.length) {
            notify(`Failed to create subscription set "${set.name}": ${setErrors[0].message}`, { type: 'error' });
            continue;
          }
          const rawSetId = setData?.insert_company_subscription_sets?.returning?.[0]?.id;
          setId = rawSetId != null ? Number(rawSetId) : (null as unknown as number);
          if (setId == null || Number.isNaN(setId)) continue;
          setsCreated += 1;
        }

        const setIdNum = Number(setId);
        if (Number.isNaN(setIdNum)) continue;

        if (set.dataPackages?.length) {
          const dataPackageRows = set.dataPackages
            .map((dp) => {
              const dataPackageId = typeof dp.id === 'string' ? parseInt(dp.id, 10) : dp.id;
              return Number.isNaN(dataPackageId)
                ? null
                : { company_subscription_set_id: setIdNum, data_package_id: dataPackageId };
            })
            .filter((row): row is { company_subscription_set_id: number; data_package_id: number } => row != null);
          if (dataPackageRows.length) {
            await insertCompanySubscriptionSetDataPackages({ variables: { objects: dataPackageRows } });
          }
        }

        if (!set.allCompanies && set.companyIds?.length) {
          const companyRows = set.companyIds
            .map((cid) => {
              const companyId = typeof cid === 'string' ? parseInt(cid, 10) : Number(cid);
              return Number.isNaN(companyId) ? null : { company_subscription_set_id: setIdNum, company_id: companyId };
            })
            .filter((row): row is { company_subscription_set_id: number; company_id: number } => row != null);
          if (companyRows.length) {
            const { data: subsData, errors: subsErrors } = await insertCompanySubscriptions({
              variables: { objects: companyRows },
            });
            if (subsErrors?.length) {
              notify(`Failed to sync company subscriptions for set "${set.name}": ${subsErrors[0].message}`, {
                type: 'error',
              });
            } else {
              companySubsCreated += subsData?.insert_company_subscriptions?.affected_rows ?? 0;
            }
          }
        }
      }

      for (const qaSet of existingQASets) {
        if (!stagingSetNames.has(qaSet.name) && !qaSet.deleted_at) {
          await deleteCompanySubscriptions({
            variables: { where: { company_subscription_set_id: { _eq: qaSet.id } } },
          });
          await deleteCompanySubscriptionSetDataPackages({
            variables: { where: { company_subscription_set_id: { _eq: qaSet.id } } },
          });
          await updateCompanySubscriptionSet({
            variables: {
              where: { id: { _eq: qaSet.id } },
              _set: { deleted_at: now, updated_at: now },
            },
          });
          setsDeleted += 1;
        }
      }

      const parts: string[] = [];
      if (setsCreated) parts.push(`${setsCreated} set(s) created`);
      if (setsUpdated) parts.push(`${setsUpdated} set(s) updated`);
      if (setsDeleted) parts.push(`${setsDeleted} set(s) soft-deleted`);
      if (companySubsCreated) parts.push(`${companySubsCreated} company subscription(s) synced`);
      notify(parts.length ? `QA synced from Staging. ${parts.join(', ')}.` : 'QA synced from Staging (no changes).', {
        type: 'success',
      });
    } catch (e) {
      console.error(e);
      notify('Failed to sync Staging to QA', { type: 'error' });
    } finally {
      setSyncToQALoading(false);
    }
  };

  const handleCreateCompanySubscriptionSetConfig = async (companySubscriptionSetID: string) => {
    try {
      await createCompanySubscriptionSetConfig(
        'company_subscription_set_configs',
        {
          data: {
            organization_id: organizationRecord.id,
            company_subscription_set_id: companySubscriptionSetID,
          },
        },
        { returnPromise: true },
      );
      redirect(
        'edit',
        'company_subscription_set_configs',
        getCompanySubscriptionSetConfigIdKey(organizationRecord.id, companySubscriptionSetID),
      );
    } catch (e) {
      console.error(e);
      notify('Error creating company subscription set config', { type: 'error' });
    }
  };

  /**
   * As QA orgs use Staging API clients, we have to fetch Company Subscription Sets from Staging
   * Client API for those orgs. If the current env is `local` or `qa`, then
   * `companySubscriptionSetsFromClientApiData` should be defined and used. Otherwise -
   * `companySubscriptionSetsFromAdminAPI` should be defined and used.
   * TODO: rm once QA Portal starts using QA Client API
   */
  const companySubscriptionSets =
    companySubscriptionSetsFromAdminAPI ??
    companySubscriptionSetsFromClientApiData?.client.companySubscriptionSets ??
    [];
  const companySubscriptionSetConfigsBySetIdMap: Partial<Record<string, CompanySubscriptionSetConfigRecord>> = {};
  companySubscriptionSetConfigs?.forEach((conf) => {
    companySubscriptionSetConfigsBySetIdMap[conf.company_subscription_set_id] = conf;
  });

  if (
    companySubscriptionSetsFromClientApiLoading ||
    externalApiClientLoading ||
    companySubscriptionSetsFromAdminAPILoading ||
    companySubscriptionSetConfigsLoading
  ) {
    return (
      <Box width="100%" textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {IS_LOCAL_OR_QA_ENV && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" sx={{ mb: 1 }}>
            ⚠️ QA ⚠️ - Subscription sets shown here are sourced from the{' '}
            <Typography fontWeight="700" component="span">
              Staging
            </Typography>{' '}
            API client used by this organization (if one exists).
          </Alert>
          {!isQaOrgWithoutStagingClient &&
            (companySubscriptionSetsFromClientApiData?.client?.companySubscriptionSets?.length ?? 0) > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Alert severity="info" sx={{ mb: 0 }}>
                  🧱 🔀 — This sync is for the{' '}
                  <Typography fontWeight="700" component="span">
                    CDC
                  </Typography>{' '}
                  (change data capture) pipeline. It copies the API client, subscription sets, and company subscriptions
                  from Staging into QA so our{' '}
                  <Typography fontWeight="700" component="span">
                    datalake
                  </Typography>{' '}
                  stays a lake and doesn’t become a datapond. 💧 Every time you add, remove, or change companies or
                  subscription sets in Staging, run this sync again so QA and the datalake stay in sync. And remember to
                  drink water and stay hydrated. 💧
                </Alert>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={syncToQALoading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                    onClick={handleSyncToQA}
                    disabled={syncToQALoading}
                    sx={{
                      bgcolor: '#DAA520',
                      color: 'black',
                      '&:hover': {
                        bgcolor: '#B8860B',
                      },
                    }}
                  >
                    {syncToQALoading
                      ? 'Syncing Staging subscription sets to QA…'
                      : 'Sync Staging subscription sets to QA'}
                  </Button>
                </Box>
              </Box>
            )}
        </Box>
      )}

      <Datagrid
        data={companySubscriptionSets}
        sort={{ field: 'id', order: 'ASC' }}
        /**
         * NOTE: <Datagrid> currently renders bulk actions by default, and <BulkActionsToolbar> uses
         * `useListContext` which will throw, since we're not in a ListContext. Therefore, we should
         * explicitly disable bulk actions here.
         */
        bulkActionButtons={false}
        empty={
          <Paper>
            {isQaOrgWithoutStagingClient ? (
              <Alert severity="warning">
                We couldn&apos;t find an API client from the{' '}
                <Typography fontWeight="700" component="span">
                  Staging
                </Typography>{' '}
                environment that matches this organization&apos;s API key.
              </Alert>
            ) : (
              <Typography variant="body2" p={2}>
                No Company Subscription Sets found
              </Typography>
            )}
          </Paper>
        }
        size="medium"
      >
        <TextField label="ID" source="id" />
        <TextField source="name" />
        <WrapperField label="Actions">
          <FunctionField
            render={(companySubscriptionSet: { id: string }) => {
              const existingConfig = companySubscriptionSetConfigsBySetIdMap[companySubscriptionSet.id];
              return existingConfig ? (
                <EditButton
                  label={editButtonLabel}
                  resource="company_subscription_set_configs"
                  record={{ id: existingConfig.id }}
                />
              ) : (
                <EditButton
                  label={editButtonLabel}
                  to="#"
                  onClick={() => handleCreateCompanySubscriptionSetConfig(companySubscriptionSet.id)}
                />
              );
            }}
          />
        </WrapperField>
      </Datagrid>

      <LoadingOverlay open={createCompanySubscriptionSetConfigLoading} />
    </>
  );
};

const CompanySubscriptionSetSettings = () => {
  const { permissions } = usePermissions();

  return (
    <CraftPageSection title="Company Subscription Set Settings" mt={4}>
      {permissions.has('companySubscriptionSet:edit') ? (
        <CompanySubscriptionSetSettingsList />
      ) : (
        <UnauthorizedSectionContent />
      )}
    </CraftPageSection>
  );
};

export default CompanySubscriptionSetSettings;
